import { CloudLightning, ShoppingCart, Activity, AlertTriangle, Radio, MapPin, Clock } from 'lucide-react';
import { Badge } from './ui/badge';

const categoryConfig = {
  'Weather': { icon: CloudLightning, color: 'border-l-[#3b82f6]', bgColor: 'bg-blue-500/10', textColor: 'text-blue-400' },
  'Supply Issues': { icon: ShoppingCart, color: 'border-l-[#f59e0b]', bgColor: 'bg-amber-500/10', textColor: 'text-amber-400' },
  'Health': { icon: Activity, color: 'border-l-[#10b981]', bgColor: 'bg-emerald-500/10', textColor: 'text-emerald-400' },
  'Unrest': { icon: AlertTriangle, color: 'border-l-[#ef4444]', bgColor: 'bg-red-500/10', textColor: 'text-red-400' },
  'Other': { icon: Radio, color: 'border-l-[#8b5cf6]', bgColor: 'bg-violet-500/10', textColor: 'text-violet-400' }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const SignalCard = ({ signal, compact = false }) => {
  const config = categoryConfig[signal.category] || categoryConfig['Other'];
  const IconComponent = config.icon;

  const location = [signal.city, signal.state, signal.country]
    .filter(Boolean)
    .join(', ');

  // Compact mode for Today tab
  if (compact) {
    return (
      <div 
        data-testid={`signal-card-compact-${signal.id}`}
        className={`bg-zinc-800/50 border border-zinc-700/50 rounded-md overflow-hidden border-l-2 ${config.color} ${signal.is_urgent ? 'ring-1 ring-red-500/30' : ''}`}
      >
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <IconComponent className={`w-3 h-3 flex-shrink-0 ${config.textColor}`} />
                <span className={`text-xs font-medium ${config.textColor}`}>
                  {signal.category}
                </span>
                {signal.is_urgent && (
                  <span className="text-xs text-red-400 font-medium">URGENT</span>
                )}
              </div>
              <p className="text-zinc-300 text-sm line-clamp-2">
                {signal.short_description}
              </p>
              <div className="flex items-center gap-2 mt-1 text-zinc-500 text-xs">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{signal.city}, {signal.state?.substring(0, 2)?.toUpperCase()}</span>
                <span>•</span>
                <span>{formatDate(signal.created_date)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Full mode (default)
  return (
    <div 
      data-testid={`signal-card-${signal.id}`}
      className={`bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-700 transition-colors border-l-4 ${config.color} ${signal.is_urgent ? 'ring-1 ring-red-500/50' : ''}`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-2 py-1 rounded-md ${config.bgColor}`}>
              <IconComponent className={`w-4 h-4 ${config.textColor}`} />
              <span className={`font-heading font-semibold text-sm tracking-tight ${config.textColor}`}>
                {signal.category}
              </span>
            </div>
            {signal.is_urgent && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs px-2 py-0.5">
                URGENT
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-zinc-500">
            <Clock className="w-3 h-3" />
            <span className="font-mono text-xs">{formatDate(signal.created_date)}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-zinc-200 text-sm leading-relaxed mb-3 line-clamp-3">
          {signal.short_description}
        </p>

        {/* Image if present */}
        {signal.image && (
          <div className="mb-3 rounded-md overflow-hidden">
            <img 
              src={signal.image} 
              alt="Signal attachment" 
              className="w-full h-40 object-cover"
              data-testid={`signal-image-${signal.id}`}
            />
          </div>
        )}

        {/* Location */}
        <div className="flex items-center gap-1 text-zinc-400">
          <MapPin className="w-3 h-3" />
          <span className="text-xs truncate">{location}</span>
        </div>
      </div>
    </div>
  );
};
